import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { TeamId } from "../interface/GameTypes";

const AUTO_DISMISS_MS = 10000;

const SUIT_SYMBOLS: Record<string, string> = {
  Spades: "♠", Hearts: "♥", Diamonds: "♦", Clubs: "♣",
};
const SUIT_COLORS: Record<string, string> = {
  Spades: "#e0e0e0", Clubs: "#e0e0e0", Hearts: "#f44336", Diamonds: "#f44336",
};

interface Props {
  suit: string;
  callerTeam: "A" | "B";
  callerName: string;
  onDismiss: () => void;
}

export const TrumpRevealModal = ({ suit, callerTeam, callerName, onDismiss }: Props) => {
  const [msLeft, setMsLeft] = useState(AUTO_DISMISS_MS);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsLeft((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          onDismiss();
          return 0;
        }
        return prev - 100;
      });
    }, 100);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const symbol = SUIT_SYMBOLS[suit] ?? suit;
  const suitColor = SUIT_COLORS[suit] ?? "#fff";
  const teamColor = callerTeam === TeamId.A ? "#4caf50" : "#ffc107";
  const progress = (msLeft / AUTO_DISMISS_MS) * 100;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
        pointerEvents: "all",
        backgroundColor: "rgba(0,0,0,0.6)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "rgba(15,15,30,0.97)",
          border: `2px solid ${suitColor}`,
          borderRadius: 3,
          padding: "28px 36px",
          textAlign: "center",
          minWidth: 260,
          maxWidth: 360,
          boxShadow: `0 0 40px ${suitColor}44`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "rgba(255,255,255,0.5)", letterSpacing: 2, fontSize: "0.65rem" }}
        >
          TRUMP SUIT
        </Typography>

        <Box
          sx={{
            fontSize: "4rem",
            lineHeight: 1.2,
            mt: 1,
            mb: 0.5,
            color: suitColor,
            textShadow: `0 0 20px ${suitColor}88`,
          }}
        >
          {symbol}
        </Box>

        <Typography
          variant="h5"
          sx={{ color: suitColor, fontWeight: "bold", mb: 1 }}
        >
          {suit}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}
        >
          Called by{" "}
          <Box component="span" sx={{ color: teamColor, fontWeight: "bold" }}>
            {callerName}
          </Box>
          {" (Team "}
          <Box component="span" sx={{ color: teamColor, fontWeight: "bold" }}>
            {callerTeam}
          </Box>
          {")"}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 2,
            borderRadius: 1,
            backgroundColor: `${suitColor}33`,
            "& .MuiLinearProgress-bar": { backgroundColor: suitColor },
          }}
        />

        <Button
          variant="contained"
          onClick={onDismiss}
          sx={{
            backgroundColor: suitColor,
            color: "#000",
            fontWeight: "bold",
            px: 3,
            "&:hover": { backgroundColor: suitColor, opacity: 0.85 },
          }}
        >
          Ready ({Math.ceil(msLeft / 1000)}s)
        </Button>

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1.5, color: "rgba(255,255,255,0.3)", fontSize: "0.6rem" }}
        >
          Waiting for all players…
        </Typography>
      </Box>
    </Box>
  );
};
